/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/toolslib/src/utils/serialize.util.ts
 */

// Extract argument and return types
// type StringifyArg = Parameters<typeof superjson.stringify>[0];
// type StringifyReturn = ReturnType<typeof superjson.stringify>['json'];

// Wrapper function preserving types
// export const stringify = (data: StringifyArg): StringifyReturn => {
// 	return superjson.serialize(data).json;
// };
// export const parse = superjson.parse;

/**
 * Normalize ISO date strings to ensure consistent precision
 * Converts dates without milliseconds to include .000
 * @param dateString - ISO date string
 * @returns Normalized ISO date string with milliseconds
 */
const normalizeISODateString = (dateString: string): string => {
  // If the date string already has milliseconds, return as-is
  if (/\.\d{3}Z?$/.test(dateString)) {
    return dateString;
  }

  // If it ends with Z but no milliseconds, add .000 before Z
  if (dateString.endsWith('Z')) {
    return dateString.replace('Z', '.000Z');
  }

  // If it has timezone offset but no milliseconds, add .000 before the offset
  if (/[+-]\d{2}:\d{2}$/.test(dateString)) {
    return dateString.replace(/([+-]\d{2}:\d{2})$/, '.000$1');
  }

  // If no timezone info, assume UTC and add .000Z
  return dateString + '.000Z';
};

export const stringify = (data: any): string => {
  return JSON.stringify(data, (_key, value) => {
    if (typeof value === 'bigint') {
      return value.toString();
    }
    if (value instanceof Date) {
      return normalizeISODateString(value.toISOString());
    }
    // Note: Do NOT normalize existing ISO date strings
    // This preserves the original format from user input
    return value;
  });
};

export const parse = (input: string): any => {
  return JSON.parse(input, (_key, value) => {
    if (typeof value === 'string') {
      // Check if this looks like a date string (ISO format)
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        const date = new Date(value);
        // Only convert if it's a valid date
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    return value;
  });
};

/**
 * Safely parse JSON with fallback handling for malformed data
 * @param item - The item to parse (can be string, null, undefined, etc.)
 * @param fallback - The fallback value to return if parsing fails
 * @returns The parsed object or the fallback value
 */
export const safeJsonParse = <T = any>(item: any, fallback: T = undefined as T): T => {
  if (!item) return fallback;

  try {
    return parse(item);
  } catch (error) {
    console.warn('Failed to parse JSON:', {
      item,
      error: error instanceof Error ? error.message : String(error)
    });
    return fallback;
  }
};

/**
 * Standardized JSON field serialization for database adapters
 * Handles the conversion between objects and JSON strings consistently
 */
export class JsonFieldSerializer {
  /**
   * Convert a value to JSON string for storage
   * Handles null/undefined by returning undefined (not null string)
   */
  static serialize(value: any): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    return stringify(value);
  }

  /**
   * Convert a JSON string back to an object
   * Handles null/undefined by returning undefined
   */
  static deserialize(value: any): any {
    if (value === null || value === undefined) {
      return undefined;
    }

    // If it's already an object, return as-is
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value;
    }

    // If it's an array, return as-is
    if (Array.isArray(value)) {
      return value;
    }

    // If it's a string, try to parse it
    if (typeof value === 'string') {
      return safeJsonParse(value, undefined);
    }

    return value;
  }

  /**
   * Convert null values to undefined for consistent handling
   * This ensures that optional fields use undefined instead of null
   */
  static normalizeNulls(value: any): any {
    if (value === null) {
      return undefined;
    }

    // Preserve Date objects as-is
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeNulls(item));
      }

      const result: any = {};
      for (const [key, val] of Object.entries(value)) {
        result[key] = this.normalizeNulls(val);
      }
      return result;
    }

    return value;
  }

  /**
   * Convert undefined values to null for database storage
   * This ensures that optional fields are stored as null in the database
   */
  static normalizeUndefineds(value: any): any {
    if (value === undefined) {
      return null;
    }

    // Preserve Date objects as-is
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeUndefineds(item));
      }

      const result: any = {};
      for (const [key, val] of Object.entries(value)) {
        result[key] = this.normalizeUndefineds(val);
      }
      return result;
    }

    return value;
  }

  /**
   * Deserialize without normalizing nulls - preserves null values
   * Use this when you want to keep null values as null
   */
  static deserializePreserveNulls(value: any): any {
    if (value === null || value === undefined) {
      return value; // Preserve null/undefined as-is
    }

    // If it's already an object, return as-is
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value;
    }

    // If it's an array, return as-is
    if (Array.isArray(value)) {
      return value;
    }

    // If it's a string, try to parse it
    if (typeof value === 'string') {
      return safeJsonParse(value, value); // Return original string if parsing fails
    }

    return value;
  }
}
